import { useProductType } from '~/composables/useProductType';
import { useTopLevelCategoryFlags } from '~/modules/featureControl/composables/useTopLevelCategoryFlags';
import { useDivisionCategoryFlags } from '~/modules/featureControl/composables/useDivisionCategoryFlags';
import { usePageData } from '~/composables/usePageData';
import UpcLatchkeyParser from './UpcLatchkeyParser';
import LatchkeyNamesParser from './LatchkeyNamesParser';
import TypeParser from './deprecated/TypeParser.mcom';
import UpcLatchkeyParserMcom from './deprecated/UpcLatchkeyParser.mcom';
import ColorParserMcom from './deprecated/ColorParser.mcom';
import type { ParsedColors } from '~/declaration/traits';
import type { ParsedLatchkeys } from '../declaration/latchkey';
import type { MemberProductMapProduct } from '~~/shared/globalTypes/product';

function defaultValues(): ParsedLatchkeys {
  return {
    latchkeyNames: [],
    latchkeyMap: {},
    latchkeySizeMap: {},
    latchkeyColorMap: {},
    latchkeyUpcMap: {},
  };
}

export default function parser(product?: Product): ParsedLatchkeys {
  const { traits } = usePageData();
  if (!product) {
    return defaultValues();
  }

  const { isConsolidatedLatchKey } = useProductType();

  if (!isConsolidatedLatchKey) {
    return defaultValues();
  }

  const latchkeyNames = LatchkeyNamesParser(product).filter((latchkey) => latchkey.toLowerCase() !== 'all items');

  const latchkeyMap: Record<LatchkeyName, MemberProductMapProduct> = {};

  /** @type {Record<string, import('@nuxt-common/media-card-large/app/modules/size/types').ParsedSizes>} */
  const latchkeySizeMap = {};

  const latchkeyColorMap: Record<string, ParsedColors> = {};

  const latchkeyUpcMap = {};

  latchkeyNames.forEach((latchkeyName) => {
    const latchkeyProduct = product.relationships.latchKeyToProductMap[latchkeyName];
    latchkeyMap[latchkeyName] = latchkeyProduct;
    latchkeySizeMap[latchkeyName] = traits?.size[latchkeyName];
    latchkeyColorMap[latchkeyName] = {
      ...traits?.color?.[latchkeyName],
      selectedColor: traits?.color?.[latchkeyName]?.colorArray[0]?.id || -1,
    };

    latchkeyUpcMap[latchkeyName] = UpcLatchkeyParser(product, latchkeyProduct);

    const { isComfortersCategory, isMenCategory, isWomenCategory, isJuniorCategory } = useTopLevelCategoryFlags();
    const { isMattressesDivision } = useDivisionCategoryFlags();

    const shouldUseSize = isMenCategory || isWomenCategory || isJuniorCategory;

    // Temp implementation for MCOM until xapi migrates to use same data struture as BCOM
    // Once migration is completed just delete this block and the imported files
    const numTypes = Object.keys(latchkeyProduct.traits.types?.typeMap || {}).length;
    if (numTypes > 0 && !shouldUseSize) {
      latchkeySizeMap[latchkeyName] = TypeParser(latchkeyProduct);
      ColorParserMcom(latchkeyColorMap, latchkeySizeMap);
      UpcLatchkeyParserMcom({
        upcMap: latchkeyUpcMap[latchkeyName].upcMap,
        colorMap: latchkeyColorMap[latchkeyName],
        sizeMap: latchkeySizeMap[latchkeyName],
        useColorArray: isMattressesDivision || isComfortersCategory,
      });
    }
  });

  return {
    latchkeyNames,
    latchkeyMap,
    latchkeySizeMap,
    latchkeyColorMap,
    latchkeyUpcMap,
  };
}
